// @flow
import { commitLocalUpdate } from 'react-relay';
import {
  type Distribution,
  learningProgressTypes,
  learningProgressType,
} from '../types';
import type {
  LearningProgressType,
} from '../types';
/* ------------------------Types------------------*/
const checkedStatus = {
  checked: 'checked',
  indeterminate: 'indeterminate',
  unchecked: 'unchecked',
};

type CheckedStatus = $Keys<typeof checkedStatus>;
type CheckBoxStatus = Distribution<CheckedStatus>;
type TagCheckBoxStatus = {
  [string]: CheckedStatus
}

/** This updates relay store locally without having to connect to server */
const updateDistLocally = (
  itemId: string,
  distribution: Distribution<number>,
  environment: *,
) => {
  commitLocalUpdate(environment, (proxy) => {
    const rootProxy = proxy.get(`${itemId}-N`);
    if (rootProxy) {
      const rootmeta = rootProxy.getLinkedRecord('metadata');
      const record = rootmeta.getLinkedRecord('distribution');
      record.setValue(distribution.low, learningProgressType.low);
      record.setValue(distribution.medium, learningProgressType.medium);
      record.setValue(distribution.high, learningProgressType.high);
    }
  });
};

const countQuestions = (
  {
    none,
    low,
    medium,
    high,
  }: Distribution<number>,
) => (
  none
  + low
  + medium
  + high
);

const countAnsweredQuestions = (
  {
    low,
    medium,
    high,
  }: Distribution<number>,
) => (
  low
    + medium
    + high
);

const distDiff = (
  dist1: Distribution<number>,
  dist2: Distribution<number>,
) => ({
  high: Math.max((dist1.high || 0 - dist2.high || 0), 0),
  none: Math.max((dist1.none || 0 - dist2.none || 0), 0),
  medium: Math.max((dist1.medium || 0 - dist2.medium || 0), 0),
  low: Math.max((dist1.low || 0 - dist2.low || 0), 0),
});

const distAdd = (
  dist1: Distribution<number>,
  dist2: Distribution<number>,
) => ({
  high: (dist1.high + dist2.high),
  none: (dist1.none + dist2.none),
  medium: (dist1.medium + dist2.medium),
  low: (dist1.low + dist2.low),
});
const distributionTypeForScore = (score: number): LearningProgressType => {
  let type = learningProgressType.none;
  if (score >= 80 && score <= 100) {
    type = learningProgressType.high;
  } else if (score < 80 && score > 50) {
    type = learningProgressType.medium;
  } else {
    type = learningProgressType.low;
  }
  return type;
};
// From questionCount -> Distribution
const calculateDistribution = (
  available: Distribution<number>,
  progressTypes: LearningProgressType[],
  questionCount: number,
  studyRecommenedTopics ?: boolean,
) => {
  const newDist = {
    none: 0,
    low: 0,
    medium: 0,
    high: 0,
  };

  let remaining = questionCount;
  const usedKeys = new Set();
  const updateDist = (key) => {
    if (remaining > 0) {
      const avail = studyRecommenedTopics && key === 'none' ? 0 : available[key];
      newDist[key] += Math.min(remaining, avail);
      remaining = Math.max(0, remaining - avail);
      usedKeys.add(key);
    }
  };
  progressTypes.forEach(updateDist);
  if (remaining > 0) {
    learningProgressTypes.forEach((k) => {
      if (!usedKeys.has(k)) {
        updateDist(k);
      }
    });
  }
  return newDist;
};

// From CheckBoxStatus -> QuestionCount
const calculateQuestionCount = (
  checkBoxStatus: CheckBoxStatus,
  available: Distribution<number>,
  required: Distribution<number>,
) => learningProgressTypes.reduce((p, i) => {
  let result = p;
  switch (checkBoxStatus[i]) {
    case checkedStatus.checked:
      result += available[i];
      break;
    case checkedStatus.indeterminate:
      result += required[i];
      break;
    default:
      break;
  }
  return result;
}, 0);

// From QuestionCount -> CheckBox
const calculateCheckBoxStatus = (
  available: Distribution<number>,
  dist: Distribution<number>,
): CheckBoxStatus => {
  const checkBoxStatus = {};
  const statusForKey = (key) => {
    if (dist[key] > 0) {
      return checkedStatus.checked;
    }
    return checkedStatus.unchecked;
  };

  learningProgressTypes.forEach((t) => {
    checkBoxStatus[t] = statusForKey(t);
  });
  return checkBoxStatus;
};

const requestedTypes = (
  checkBoxStatus: CheckBoxStatus,
) => learningProgressTypes.filter(
  l => checkBoxStatus[l] === checkedStatus.checked,
);

// When item count is updated
// - update the distribution
// - update the checkbox status
const onQuestionCountUpdated = (
  available: Distribution<number>,
  checkBoxStatus: CheckBoxStatus,
  requiredItems: number,
  optimalDefault?: ?number,
  studyRecommenedTopics?: boolean,
) => {
  const requestedStatus = requestedTypes(checkBoxStatus);
  let optimalDist;
  if (optimalDefault) {
    optimalDist = calculateDistribution(
      available,
      requestedStatus,
      optimalDefault,
    );
  }
  const forCheckboxDist = calculateDistribution(
    available,
    requestedStatus,
    requiredItems,
    studyRecommenedTopics,
  );
  const newStatus = calculateCheckBoxStatus(
    available,
    forCheckboxDist,
  );
  return {
    questionCount: requiredItems,
    checkBoxStatus: newStatus,
    distribution: optimalDist || forCheckboxDist,
  };
};

const onCheckBoxStatusUpdated = (
  available: Distribution<number>,
  distribution: Distribution<number>,
  checkBoxStatus: CheckBoxStatus,
) => {
  const questionCount = calculateQuestionCount(
    checkBoxStatus,
    available,
    distribution,
  );
  const requestedStatus = requestedTypes(checkBoxStatus);
  const newDistribution = calculateDistribution(
    available,
    requestedStatus,
    questionCount,
  );
  return {
    questionCount,
    checkBoxStatus,
    distribution: newDistribution,
  };
};

const shouldExpand = (
  distribution: Distribution<number>,
  minQuestions: number,
  expanded: boolean,
): boolean => {
  const qCount = countQuestions(distribution);
  /*
      expand filter by progress
      if questions count is < minimum required
    */
  let mustExpand = expanded;
  if (qCount < minQuestions) {
    mustExpand = true;
  }
  return mustExpand;
};

export type {
  CheckBoxStatus,
  TagCheckBoxStatus,
};

export {
  checkedStatus,
  countQuestions,
  countAnsweredQuestions,
  onQuestionCountUpdated,
  onCheckBoxStatusUpdated,
  shouldExpand,
  distributionTypeForScore,
  distAdd,
  distDiff,
  updateDistLocally,
};
